import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import { Toaster } from "./components/ui/sonner";
import AuthCallback from "./components/AuthCallback";
import NotificationCenter from "./components/NotificationCenter";
import { MobileFrame } from "./components/Shell";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Swipe from "./pages/Swipe";
import Matches from "./pages/Matches";
import ChatRoom from "./pages/ChatRoom";
import Profile from "./pages/Profile";
import VerifyAadhaar from "./pages/VerifyAadhaar";
import Earnings from "./pages/Earnings";
import Admin from "./pages/Admin";
import { Loader2 } from "lucide-react";

function Splash() {
  return (
    <div className="h-[100dvh] w-full flex items-center justify-center bg-[#0D0C12]">
      <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
    </div>
  );
}

function Protected({ requireComplete = true, adminOnly = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/auth" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/" replace />;
  if (!user.is_admin) {
    // 1. Profile must be complete.
    if (!user.profile_complete && location.pathname !== "/profile") {
      return <Navigate to="/profile" replace />;
    }
    // 2. After a complete profile, Aadhaar upload is mandatory before the app.
    //    (pending users keep full access; none/rejected are sent to the verify step,
    //    from where Back returns one step to /profile.)
    if (user.profile_complete && ["none", "rejected"].includes(user.aadhaar_status || "none")
        && location.pathname !== "/verify-aadhaar" && location.pathname !== "/profile") {
      return <Navigate to="/verify-aadhaar" replace />;
    }
  }
  return <Outlet />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const routes = (
    <Routes>
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Protected requireComplete={false} />}>
        <Route path="/profile" element={<Profile />} />
        <Route path="/verify-aadhaar" element={<VerifyAadhaar />} />
      </Route>
      <Route element={<Protected />}>
        <Route path="/" element={<Swipe />} />
        <Route path="/matches" element={<Matches />} />
        <Route path="/chat/:matchId" element={<ChatRoom />} />
        <Route path="/earnings" element={<Earnings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
  // The Admin Panel is a completely separate app with its own URL + login.
  // It is NOT part of the user website routes and is never linked from the user UI.
  if (location.pathname.startsWith("/control-center")) {
    return <Admin />;
  }
  return (
    <MobileFrame>
      <NotificationCenter />
      {routes}
    </MobileFrame>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SocketProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-center" theme="dark" />
        </SocketProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
